import * as React from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { match as MatchProps, Route } from "react-router";

import ServiceList from "./ServiceList";
import PackageInfo from "./PackageInfo";
import ProductInfo from "./ProductInfo";
import { RootState } from "../../store";
import ProductCreate from "./ProductCreate";
import PackageCreate from "./PackageCreate";
import { getServices } from "../../actions/services";
import { getPublicService } from "../../slices/publicServiceSlice";
import { getCurrentSubscription } from "../../slices/subscriptionSlice";
import { ProductInterface } from "../../interfaces/ProductInterface";

export const SSF_SERVICE_ORIGIN = "ssf";

export enum PublicServiceStatus {
  PUBLIC = "Public",
  NOTPUBLIC = "Not Public"
}

const Service = ({
  match,
  isPublicTab = false,
  isSsfTab = false,
  isInactiveTab
}: {
  match: MatchProps;
  isPublicTab?: boolean;
  isSsfTab?: boolean;
  isInactiveTab?: boolean;
}): React.ReactNode => {
  const dispatch = useDispatch();

  // selectors
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentreId);
  const services = useSelector((state: RootState) => state.services.collection) || [];
  const resourceCentre = useSelector((state: RootState) => state.userContext.resourceCentre);
  const publicServices = useSelector((state: RootState) => state.publicServices.collection) || [];
  const publicBookingEnabled =
    useSelector((state: RootState) => state.resources.resourceCentres[0]?.publicBooking) || false;
  const hasCurrentSubscription = useSelector(
    (state: RootState) => state.subscriptions.currentSubscription
  );

  // local states
  const [selectedRow, setSelectedRow] = React.useState<ProductInterface | null>(null);

  // helper functions
  const handleClose = () => {
    setSelectedRow(null);
  };

  // variables
  const activeSortedServices = services
    .filter(({ active }) => active)
    .sort((a, b) => (`${a.name || ""}`.toLowerCase() > `${b.name || ""}`.toLowerCase() ? 1 : -1));

  const activePublicServices = publicServices.filter((service) => service.active);

  let servicesFiltered;
  if (isPublicTab) {
    servicesFiltered = activeSortedServices.filter((service) => service.referenceId);
    const referenceIds = servicesFiltered.map((service) => service.referenceId);
    servicesFiltered = [
      ...servicesFiltered,
      ...activePublicServices.filter((pbService) => !referenceIds.includes(pbService.id))
    ];
  } else {
    servicesFiltered = activeSortedServices.filter((service) => !service.referenceId);
  }

  // get ssf subscription from settings
  const isSsfSubscribed = resourceCentre?.subscriptions?.features?.ssf?.subscribed || false;

  if (isSsfTab) {
    if (!isSsfSubscribed) {
      servicesFiltered = [];
    } else {
      servicesFiltered = activeSortedServices.filter(({ origin }) => origin === SSF_SERVICE_ORIGIN);
    }
  } else {
    servicesFiltered = activeSortedServices.filter(
      ({ referenceId, origin }) => !referenceId && origin !== SSF_SERVICE_ORIGIN
    );
  }

  if (isInactiveTab) {
    servicesFiltered = services.filter(({ active }) => !active);
  }

  // effects
  React.useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  React.useEffect(() => {
    if (!hasCurrentSubscription) {
      dispatch(getCurrentSubscription(rcId));
    }
  }, [dispatch, hasCurrentSubscription, rcId]);

  React.useEffect(() => {
    if (publicBookingEnabled) {
      dispatch(getPublicService());
    }
  }, [publicBookingEnabled, dispatch]);

  if (isPublicTab && !publicBookingEnabled) return null;

  return (
    <Box height="100%">
      <ServiceList
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        isPublicTab={isPublicTab}
        isSsfTab={isSsfTab}
        isInactiveTab={isInactiveTab}
        servicesFiltered={servicesFiltered}
      />

      <Route
        exact
        path={`${match.path}/newProduct`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        render={(props) => <ProductCreate {...props} isSsfTab={isSsfTab} />}
      />

      <Route
        exact
        path={`${match.path}/newPackage`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        render={(props) => <PackageCreate {...props} />}
      />

      <Route
        exact
        path={`${match.path}/product/:id/edit`}
        render={(props) => (
          <ProductCreate
            productId={Number(props.match.params.id)}
            isPublicTab={isPublicTab}
            handleClose={handleClose}
            isSsfTab={isSsfTab}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />

      <Route
        exact
        path={`${match.path}/package/:id/edit`}
        render={(props) => (
          <PackageCreate
            packageId={Number(props.match.params.id)}
            handleClose={handleClose}
            isPublicTab={isPublicTab}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />

      <Route
        exact
        path={`${match.path}/product/:id`}
        render={(props) => (
          <ProductInfo
            productId={Number(props.match.params.id)}
            handleClose={handleClose}
            isPublicTab={isPublicTab}
            isSsfTab={isSsfTab}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />

      <Route
        exact
        path={`${match.path}/package/:id`}
        render={(props) => (
          <PackageInfo
            packageId={Number(props.match.params.id)}
            handleClose={handleClose}
            isPublicTab={isPublicTab}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      />
    </Box>
  );
};

export default Service;
